/**
 * Google Ads Conversion Tracking mit Consent Mode v2 (DSGVO-konform)
 *
 * - Default-Werte sind DENIED (kein Tracking ohne Consent)
 * - Der Tag wird erst nachgeladen, wenn der Nutzer Marketing-Cookies akzeptiert
 * - Die Conversion wird nur EINMAL nach erfolgreichem Formularversand ausgelöst
 */

const GOOGLE_ADS_ID = 'AW-18108998224';
const CONVERSION_EVENT = 'ads_conversion_Submit_lead_form_1';
const CONVERSION_FIRED_KEY = 'lead_conversion_fired';
const CONSENT_STORAGE_KEY = 'cookieConsent';
// 12 Monate Consent-Gültigkeit (DSK-Empfehlung)
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

let gtagScriptLoaded = false;

/**
 * Initialisiert dataLayer + gtag-Stub und setzt Consent Mode v2 Defaults auf "denied".
 * Wird beim App-Start aufgerufen – noch BEVOR ein Skript von Google geladen wird.
 */
export const initConsentMode = () => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  // gtag-Stub, falls noch kein Script geladen ist
  if (typeof window.gtag !== 'function') {
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }

  // Consent Mode v2 Defaults – alle Marketing-/Analytics-Speicher deaktiviert
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
};

/**
 * Lädt gtag.js, falls noch nicht geschehen.
 */
const loadGtagScript = () => {
  if (typeof window === 'undefined' || gtagScriptLoaded) return;
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
    gtagScriptLoaded = true;
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', GOOGLE_ADS_ID);

  gtagScriptLoaded = true;
};

/**
 * Erlaubt Marketing-/Ads-Tracking. Aktualisiert Consent + lädt gtag.js nach.
 */
export const grantMarketingConsent = () => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  window.gtag('consent', 'update', {
    ad_storage: 'granted',
    analytics_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });

  loadGtagScript();
};

/**
 * Widerruft Marketing-Consent. Tracking wird unterbunden.
 * (Der Tag bleibt geladen, sendet aber dank Consent Mode keine Daten mehr.)
 */
export const revokeMarketingConsent = () => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  window.gtag('consent', 'update', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
};

/**
 * Feuert die Lead-Conversion – nur einmal pro Browser-Session.
 * Sicher gegenüber fehlendem window.gtag.
 */
export const trackLeadConversion = () => {
  if (typeof window === 'undefined') return;

  // Idempotenz: nur einmal pro Session feuern
  try {
    if (sessionStorage.getItem(CONVERSION_FIRED_KEY) === '1') return;
  } catch (e) {
    // sessionStorage kann blockiert sein – dann lassen wir die Conversion durch
  }

  if (typeof window.gtag !== 'function') return;

  window.gtag('event', CONVERSION_EVENT, {
    send_to: GOOGLE_ADS_ID,
  });

  try {
    sessionStorage.setItem(CONVERSION_FIRED_KEY, '1');
  } catch (e) {
    // ignorieren
  }
};

/**
 * Liefert gespeicherte Consent-Daten zurück – aber nur, wenn sie nicht abgelaufen
 * sind (12 Monate). Abgelaufene Einträge werden entfernt und null zurückgegeben.
 */
export const getStoredConsent = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.timestamp) return null;
    const age = Date.now() - new Date(data.timestamp).getTime();
    if (Number.isNaN(age) || age > CONSENT_TTL_MS) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
};

/**
 * Liest gespeicherte Consent-Präferenzen und stellt sie bei Seiten-Reload wieder her.
 * Wird beim App-Start aufgerufen. Berücksichtigt Ablaufdatum.
 */
export const restoreConsentFromStorage = () => {
  if (typeof window === 'undefined') return;
  const data = getStoredConsent();
  if (data?.preferences?.marketing === true) {
    grantMarketingConsent();
  }
};
